import {
  QueryKey,
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query'

import { EndpointProviderDefaults } from './convertEndpointProviderToFetchArgs'
import { EndpointInterface } from './Endpoint'
import { defaultErrorBoundaryHandler } from './helpers/defaultErrorBoundaryHandler'
import { useNetworkContext } from './NetworkProvider'
import { useEndpointFetch } from './useEndpointFetch'

type EndpointProviderFactory<T = any> = (...args: any) => EndpointInterface<T>

type EndpointReturnType<T> = T extends EndpointProviderFactory<infer R>
  ? R
  : unknown

type UseEndpointOptions<TResponse> = Partial<{
  opts: Omit<UseQueryOptions<TResponse>, 'queryKey'>
  defaults: EndpointProviderDefaults
  isBlob?: boolean
}>

export type UseEndpointOptionsFromProvider<
  EndpointFactory extends EndpointProviderFactory<any>,
> = UseEndpointOptions<EndpointReturnType<EndpointFactory>>

export function useEndpoint<
  TResponse = any,
  TRequestBody extends Record<string, any> = Record<string, any>,
>(
  name: QueryKey,
  endpoint: EndpointInterface<TResponse, TRequestBody>,
  { opts, defaults, isBlob = false }: UseEndpointOptions<TResponse> = {},
): UseQueryResult<TResponse> {
  const { errorBoundaryHandler } = useNetworkContext()
  const endpointFetch = useEndpointFetch({ endpoint, defaults, isBlob })

  return useQuery({
    queryKey: name,
    queryFn: endpointFetch,
    retry: 1,
    refetchOnWindowFocus: false,
    ...opts,
    throwOnError:
      opts?.throwOnError ??
      (errorBoundaryHandler || defaultErrorBoundaryHandler),
  })
}
